"use client";
import Fuse from "fuse.js";
import { useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toPlainText } from "next-sanity";
import { Reveal } from "../reveal";
import { ExtractBySectionType, SectionType } from "types";
import { PortableText } from "next-sanity";
import { FAQs, FAQsType } from "../shared/faqs";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Form, Formik } from "formik";
import { TextInput } from "../inputs/text-input";
import { Search } from "../icons/search";
type FAQsTabsSectionType = ExtractBySectionType<SectionType, "faqTabs", "_type">;

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function FAQsTabs({ data }: { data: FAQsTabsSectionType }) {
  const { heading, tabItems, description } = data;
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<FAQsType | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const router = useRouter();
  const searchParams = useSearchParams();

  const fuse = useMemo(() => {
    const allFaqs = tabItems.flatMap((tab) =>
      tab.faqs.map((faq) => ({
        ...faq,
        tabId: tab._id,
        plainAnswer: Array.isArray(faq.answer) ? toPlainText(faq.answer) : faq.answer,
        tabName: tab.name,
      })),
    );

    const options = {
      keys: [
        { name: "question", weight: 0.7 }, // Questions are more important
        { name: "plainAnswer", weight: 0.3 },
      ],
      threshold: 0.2, // 0 = perfect match, 1 = match anything
      distance: 100, // smaller distance for better precision
      includeMatches: true,
      includeScore: true,
      minMatchCharLength: 2,
      ignoreLocation: false,
      findAllMatches: true,
      shouldSort: true,
    };

    return new Fuse(allFaqs, options);
  }, [tabItems]);

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");

    if (!tabFromUrl) return;

    const tabIndex = tabItems.findIndex(
      (tab) => tab._id === tabFromUrl || tab.slug.current === tabFromUrl,
    );

    if (tabIndex !== -1) {
      setCurrentTabIndex(tabIndex);
    }
  }, [searchParams, tabItems]);

  useEffect(() => {
    if (!debouncedSearchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    // Simulate slight delay for better UX
    const searchTimeout = setTimeout(() => {
      const results = fuse.search(debouncedSearchTerm);
      const faqs = results.map((result) => result.item);
      setSearchResults(faqs);
    }, 100);

    return () => clearTimeout(searchTimeout);
  }, [debouncedSearchTerm, fuse]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const getTabClickHandler = (faqs: (typeof tabItems)[0], index: number) => {
    return () => {
      setCurrentTabIndex(index);
      router.replace(`?tab=${faqs.slug.current}`, { scroll: false });
    };
  };

  return (
    <section className="max-w-lg md:max-w-4xl lg:max-w-6xl xl:max-w-screen-2xl mx-auto py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8  bg-white">
      <Reveal>
        <h3 className="font-medium text-lg sm:text-xl md:text-2xl lg:text-3xl">{heading}</h3>
        {description && (
          <PortableText
            value={description}
            components={{
              block: {
                normal: ({ children }) => (
                  <p className="max-w-screen-sm mt-2 mb-8 md:mb-12 lg:mb-16 text-xs sm:text-base md:text-lg lg:text-xl">
                    {children}
                  </p>
                ),
              },
            }}
          />
        )}
      </Reveal>
      <Formik
        initialValues={{ search: "" }}
        onSubmit={() => {
          return;
        }}
      >
        <Form className="flex flex-col items-center justify-center gap-8 py-10">
          <div className="relative w-full max-w-screen-sm">
            <TextInput
              name="search"
              label=""
              className="w-full"
              placeholder="Ask anything..."
              onChange={handleSearchChange}
            />
            <Search className="absolute right-8 bottom-5" />
          </div>
        </Form>
      </Formik>

      <Reveal initialY={"10%"}>
        {searchResults ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={"itadori"}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col md:flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-24"
            >
              <FAQs faqs={searchResults} />
            </motion.div>
          </AnimatePresence>
        ) : (
          <Tabs
            defaultValue={tabItems[0]._id}
            value={tabItems[currentTabIndex]._id}
            className="flex flex-col md:flex-row xl:flex-row gap-4 md:gap-12 lg:gap-16 xl:gap-24 justify-center"
          >
            <ScrollArea className="w-[calc(100vw_-_32px)] md:w-fit whitespace-nowrap md:mx-0 px-2 md:px-0">
              <TabsList className="flex md:flex-col xl:flex-col flex-row h-full bg-white items-start gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible">
                {tabItems.map((faqs, index) => (
                  <TabsTrigger value={faqs._id} key={faqs._id} asChild>
                    <button
                      onClick={getTabClickHandler(faqs, index)}
                      className="relative py-2 px-3 md:px-4 text-xs md:text-sm lg:text-base gap-2 cursor-pointer data-[state=active]:text-white group data-[state=active]:bg-transparent data-[state=active]:shadow-none transition duration-500 whitespace-nowrap md:whitespace-normal hover:bg-primary-400/80 hover:text-white"
                    >
                      {currentTabIndex === index && (
                        <motion.div
                          layoutId="solution"
                          className={`absolute h-full w-full group-data-[state=active]:bg-primary-400 group-data-[state=active]:shadow-sm group-data-[state=active]:rounded-sm group-data-[state=active]:hover:bg-primary-400/80`}
                        />
                      )}
                      <span className="relative">{faqs.name}</span>
                    </button>
                  </TabsTrigger>
                ))}
              </TabsList>

              <ScrollBar orientation="horizontal" className="h-0.5" />
            </ScrollArea>
            {tabItems.map((faqs) => (
              <TabsContent value={faqs._id} key={faqs._id} className="flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={faqs._id}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col md:flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-24"
                  >
                    <FAQs faqs={faqs.faqs} />
                  </motion.div>
                </AnimatePresence>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </Reveal>
    </section>
  );
}
